import React, { FC, ReactNode, useRef, useState } from 'react';
import classnames from 'classnames';

import { useBoundingClientRect } from '../../../hooks/skin/use-bounding-client-rect';

import { ProgressBar } from './ui/progress-bar/component';
import { BufferedRangesBar } from './ui/buffered-ranges-bar/component';
import { RemainingTime } from './ui/remaining-time/RemainingTime';
import { HoverBar } from './ui/hover-bar/component';
import { Time } from './types/time';
import styles from './styles.module.css';

interface Props {
    className?: string;
    renderCursor?: (time: Time) => ReactNode;
}

export const Timeline: FC<Props> = ({ className, renderCursor }) => {
    const sliderRef = useRef<HTMLDivElement>(null);

    const { boundingClientRectState, recalculateBoundingClientRect } =
        useBoundingClientRect(sliderRef);

    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [isDragged, setIsDragged] = useState<boolean>(false);

    return (
        <div
            className={classnames(styles.root, className, {
                [styles.active]: isHovered || isDragged,
            })}
            dir="ltr"
        >
            <div
                ref={sliderRef}
                className={styles.slider}
                onMouseEnter={() => {
                    recalculateBoundingClientRect();
                    setIsHovered(true);
                }}
                onMouseLeave={() => setIsHovered(false)}
            >
                <BufferedRangesBar className={styles.bufferedRangesBar} />
                {(isHovered || isDragged) && (
                    <HoverBar
                        isDragged={isDragged}
                        timelineWidth={boundingClientRectState?.width}
                        containerLeftPadding={boundingClientRectState?.left}
                        className={styles.hoverBar}
                        renderCursor={renderCursor}
                    />
                )}
                <ProgressBar
                    className={styles.progress}
                    timelineWidth={boundingClientRectState?.width}
                    containerLeftPadding={boundingClientRectState?.left}
                    onDragStateChanged={setIsDragged}
                    isTimelineHovered={isHovered}
                />
            </div>
            <RemainingTime className={styles.time} />
        </div>
    );
};
